import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Pencil, Save, X, ExternalLink, FileText } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = {
  type: "article", title: "", slug: "", excerpt: "", meta_description: "",
  cover_image: "", tags: "", content: "", media: "", results: "", client_name: "", published: true,
};

// media lines: "type|url|caption"  → [{type,url,caption}]
const parseMedia = (s) => (s || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
  const [type, url, caption] = l.split("|").map((x) => (x || "").trim());
  return { type: type === "video" ? "video" : "image", url, caption: caption || "" };
}).filter((m) => m.url);
const stringifyMedia = (arr) => (arr || []).map((m) => `${m.type}|${m.url}|${m.caption || ""}`).join("\n");

// results lines: "label|value"
const parseResults = (s) => (s || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
  const [label, value] = l.split("|").map((x) => (x || "").trim());
  return { label, value };
}).filter((r) => r.label);
const stringifyResults = (arr) => (arr || []).map((r) => `${r.label}|${r.value}`).join("\n");

export default function BlogAdmin() {
  const { user } = useAuth();
  const [posts, setPosts] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/blog/admin/posts").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (user && user.role !== "admin") {
    return <div className="glass rounded-2xl p-12 text-center border border-border max-w-xl mx-auto" data-testid="blog-admin-guard">هذه الصفحة متاحة للمشرف فقط.</div>;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startNew = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };

  const startEdit = async (id) => {
    try {
      const { data } = await api.get(`/blog/admin/posts/${id}`);
      setForm({
        type: data.type, title: data.title, slug: data.slug, excerpt: data.excerpt || "",
        meta_description: data.meta_description || "", cover_image: data.cover_image || "",
        tags: (data.tags || []).join(", "), content: data.content || "",
        media: stringifyMedia(data.media), results: stringifyResults(data.results),
        client_name: data.client_name || "", published: data.published !== false,
      });
      setEditingId(id); setShowForm(true);
      window.scrollTo(0, 0);
    } catch (e) { toast.error("تعذّر تحميل المقال"); }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("أدخل عنوان المقال");
    setSaving(true);
    const payload = {
      type: form.type, title: form.title, slug: form.slug, excerpt: form.excerpt,
      meta_description: form.meta_description, cover_image: form.cover_image,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      content: form.content, media: parseMedia(form.media), results: parseResults(form.results),
      client_name: form.client_name, published: form.published,
    };
    try {
      if (editingId) { await api.put(`/blog/posts/${editingId}`, payload); toast.success("تم تحديث المقال ✅"); }
      else { await api.post("/blog/posts", payload); toast.success("تم نشر المقال ✅"); }
      setShowForm(false); setForm(EMPTY); setEditingId(null); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف هذا المقال نهائياً؟")) return;
    try { await api.delete(`/blog/posts/${id}`); toast.success("تم الحذف"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="blog-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold font-heading flex items-center gap-2"><FileText className="w-7 h-7 text-primary" /> إدارة المدونة</h1>
          <p className="text-muted-foreground">أضِف وعدّل مقالات التسويق وقصص النجاح التي تظهر للزوار في صفحة المدونة.</p>
        </div>
        <div className="flex gap-2">
          <a href="/blog" target="_blank" rel="noopener noreferrer"><Button variant="outline" className="gap-2 border-border"><ExternalLink className="w-4 h-4" /> عرض المدونة</Button></a>
          <Button onClick={startNew} className="bg-primary hover:bg-primary/90 text-white gap-2" data-testid="blog-admin-new"><Plus className="w-4 h-4" /> مقال جديد</Button>
        </div>
      </div>

      {showForm && (
        <div className="glass rounded-2xl p-6 border border-primary/30" data-testid="blog-admin-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold">{editingId ? "تعديل مقال" : "مقال جديد"}</h2>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label className="mb-2 block">النوع</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="bg-secondary border-border" data-testid="blog-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="article">مقال</SelectItem>
                  <SelectItem value="success_story">قصة نجاح</SelectItem>
                  <SelectItem value="guide">دليل / فيديو إرشادي</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-secondary px-3 border border-border">
              <Label>منشور (ظاهر للزوار)</Label>
              <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="blog-published-toggle" />
            </div>
            <div className="md:col-span-2"><Label className="mb-2 block">العنوان</Label>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-secondary border-border" data-testid="blog-title-input" /></div>
            <div><Label className="mb-2 block">الرابط (slug) — اختياري</Label>
              <Input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="يتولّد تلقائياً من العنوان" dir="ltr" className="bg-secondary border-border" /></div>
            <div><Label className="mb-2 block">رابط صورة الغلاف</Label>
              <Input value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} dir="ltr" className="bg-secondary border-border" data-testid="blog-cover-input" /></div>
            <div className="md:col-span-2"><Label className="mb-2 block">مقتطف قصير (excerpt)</Label>
              <Textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} className="bg-secondary border-border" /></div>
            <div className="md:col-span-2"><Label className="mb-2 block">وصف محركات البحث (meta description)</Label>
              <Textarea value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} rows={2} className="bg-secondary border-border" data-testid="blog-meta-input" /></div>
            <div className="md:col-span-2"><Label className="mb-2 block">الوسوم/الهاشتاقات (مفصولة بفاصلة)</Label>
              <Input value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="#تسويق, #السعودية" className="bg-secondary border-border" data-testid="blog-tags-input" /></div>
            {form.type === "success_story" && (
              <>
                <div><Label className="mb-2 block">اسم العميل</Label>
                  <Input value={form.client_name} onChange={(e) => set("client_name", e.target.value)} className="bg-secondary border-border" /></div>
                <div><Label className="mb-2 block">النتائج — سطر لكل نتيجة: التسمية|القيمة</Label>
                  <Textarea value={form.results} onChange={(e) => set("results", e.target.value)} rows={3} placeholder={"نمو التفاعل|+320%\nمدة التنفيذ|شهران"} dir="rtl" className="bg-secondary border-border" /></div>
              </>
            )}
            <div className="md:col-span-2"><Label className="mb-2 block">الوسائط — سطر لكل عنصر: النوع|الرابط|التعليق (النوع: image أو video)</Label>
              <Textarea value={form.media} onChange={(e) => set("media", e.target.value)} rows={3} placeholder={"image|https://...jpg|أجواء المقهى\nvideo|https://...mp4|جولة في المنصة"} dir="ltr" className="bg-secondary border-border" data-testid="blog-media-input" /></div>
            <div className="md:col-span-2"><Label className="mb-2 block">المحتوى — افصل الفقرات بسطر فارغ، وابدأ العناوين بـ ## </Label>
              <Textarea value={form.content} onChange={(e) => set("content", e.target.value)} rows={12} className="bg-secondary border-border leading-relaxed" data-testid="blog-content-input" /></div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-white gap-2" data-testid="blog-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editingId ? "حفظ التعديلات" : "نشر المقال"}
            </Button>
            <Button variant="outline" onClick={() => setShowForm(false)} className="border-border">إلغاء</Button>
          </div>
        </div>
      )}

      {posts === null ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : posts.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center border border-border text-muted-foreground">لا توجد مقالات بعد. أضِف أول مقال.</div>
      ) : (
        <div className="space-y-2" data-testid="blog-admin-list">
          {posts.map((p) => (
            <div key={p.id} className="glass rounded-xl p-4 border border-border flex items-center gap-4" data-testid={`blog-admin-item-${p.slug}`}>
              {p.cover_image && <img src={p.cover_image} alt="" className="w-16 h-16 rounded-lg object-cover border border-border shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${p.type === "success_story" ? "bg-accent/15 text-accent" : p.type === "guide" ? "bg-emerald-500/15 text-emerald-400" : "bg-primary/15 text-primary"}`}>{p.type === "success_story" ? "قصة نجاح" : p.type === "guide" ? "دليل/فيديو" : "مقال"}</span>
                  {!p.published && <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">مسودة</span>}
                  <span className="font-bold text-sm truncate">{p.title}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 truncate" dir="ltr">/blog/{p.slug}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => startEdit(p.id)} className="border-border gap-1 h-8" data-testid={`blog-edit-${p.slug}`}><Pencil className="w-3.5 h-3.5" /> تعديل</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive h-8" data-testid={`blog-delete-${p.slug}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
